import { useState, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { State } from 'index'
import { getStoreShippings } from 'reducks/storeShipping/selectors'
import { StoreShippingType } from 'types/storeShipping'
import { CheckoutStatusType } from 'types/checkout'
import { addDays, endOfToday, isBefore } from 'date-fns'
import { parseToDate } from 'lib/helper'

const VALID_STATUSES: CheckoutStatusType[] = ['succeeded', 'requires_capture']

const usePreparingShippingCount = (daysAhead: number): { count: number; loading: boolean } => {
  const storeShippings = useSelector((state: State) => getStoreShippings(state))
  const [checkoutStatuses, setCheckoutStatuses] = useState<Map<string, CheckoutStatusType>>(new Map())
  const [loading, setLoading] = useState(true) // 初期値をtrueに（最初のレンダーでcount: 0が返されるのを防ぐ）
  const fetchIdRef = useRef(0) // race condition対策

  // 日付範囲内のshippingsをフィルタ
  const relevantShippings = useMemo(() => {
    return storeShippings.filter((shipping: StoreShippingType) =>
      isBefore(parseToDate(shipping.scheduled_delivery_date), addDays(endOfToday(), daysAhead))
    )
  }, [storeShippings, daysAhead])

  // checkout statusを取得
  useEffect(() => {
    let isActive = true // クリーンアップ用フラグ
    const currentFetchId = ++fetchIdRef.current // 新しいfetchIDを発行

    if (relevantShippings.length === 0) {
      setCheckoutStatuses(new Map())
      setLoading(false) // Loading状態をリセット
      return // 早期リターン時はクリーンアップ不要（非同期処理なし）
    }

    const fetchStatuses = async () => {
      // 未取得のcheckoutIdだけ抽出
      const missingCheckoutRefs = new Map(
        relevantShippings
          .filter((shipping: StoreShippingType) => !checkoutStatuses.has(shipping.store_checkout_ref.id))
          .map((shipping: StoreShippingType) => [shipping.store_checkout_ref.id, shipping.store_checkout_ref])
      )

      // 全て取得済みなら何もしない
      if (missingCheckoutRefs.size === 0) {
        setLoading(false)
        return
      }

      setLoading(true)
      const newStatusMap = new Map<string, CheckoutStatusType>()

      await Promise.all(
        Array.from(missingCheckoutRefs.entries()).map(async ([checkoutId, checkoutRef]) => {
          try {
            const doc = await checkoutRef.get()
            if (doc.exists) {
              newStatusMap.set(checkoutId, doc.data()?.status)
            }
          } catch (e) {
            console.error('Failed to fetch checkout status:', e)
          }
        })
      )

      // race condition対策 + アンマウント対策
      if (isActive && currentFetchId === fetchIdRef.current) {
        // 既存のステータスと新規取得分をマージ
        setCheckoutStatuses((prev) => {
          const merged = new Map(prev)
          newStatusMap.forEach((value, key) => merged.set(key, value))
          return merged
        })
        setLoading(false)
      }
    }

    fetchStatuses()

    // クリーンアップ関数
    return () => {
      isActive = false
    }
  }, [relevantShippings])

  // 決済完了済みのみカウント
  const count = useMemo(() => {
    return relevantShippings.filter((shipping: StoreShippingType) => {
      const status = checkoutStatuses.get(shipping.store_checkout_ref.id)
      return status && VALID_STATUSES.includes(status)
    }).length
  }, [relevantShippings, checkoutStatuses])

  return { count, loading }
}

export default usePreparingShippingCount
